import { IOutcomeBreakdownTableProps } from "./definitions";
import React, { useEffect, useState } from "react";
import styles from "./style.module.scss";
import { useCollection } from "@amzn/awsui-collection-hooks";
import { Box, Header, Popover, Table } from "@amzn/awsui-components-react";
import { SpaceBetween } from "@amzn/awsui-components-react/polaris";
import { TableProps } from "@amzn/awsui-components-react";
import {
  TaxYearBreakdown,
  TaxAreaBreakdown,
  TaxYearAreaBreakdown,
} from "models/schemas";
import { formatMonetaryValue } from "pages/audit-overview/definitions";
import { useTeamNameSelector } from "pages/team_settings/hooks";
import { ListTaxAreaRequest } from "pages/team_settings/definitions";
import { useListTaxAreaByTeamQuery } from "pages/team_settings/endpoints";
import { createLabelFunction } from "utils/table-utils";
import {
  INACTIVE_TAX_AREA_STRING,
  TableLabels,
  LabelPopovers,
} from "./constants";
import { GaiaConstants } from "utils";
import { formatDateValue } from "utils/date-format";

const OutcomeBreakdownTable: React.FC<IOutcomeBreakdownTableProps> = (
  props: IOutcomeBreakdownTableProps
) => {
  const [selectedItems, setSelectedItems] = useState<
    (TaxYearBreakdown | TaxAreaBreakdown | TaxYearAreaBreakdown)[]
  >([]);
  const { teamName } = useTeamNameSelector();
  const [taxAreasMap, setTaxAreasMap] = useState<
    { label: string; value: string }[]
  >([]);
  const listTaxAreaRequest: ListTaxAreaRequest = {
    teamName: teamName,
  };
  const { data: listTaxAreaData } = useListTaxAreaByTeamQuery(
    listTaxAreaRequest,
    {
      refetchOnMountOrArgChange: true,
      skip: !teamName || teamName === GaiaConstants.PLACE_HOLDER,
    }
  );

  useEffect(() => {
    if (listTaxAreaData?.taxAreas) {
      setTaxAreasMap(
        listTaxAreaData.taxAreas.taxAreas.map((taxArea) => ({
          value: taxArea.taxAreaId,
          label: taxArea.title,
        }))
      );
    }
  }, [listTaxAreaData]);

  const calculateErrorRate = (
    amountReceived: string,
    amountInError: string
  ): string => {
    const received = Number(amountReceived) || 0;
    const inError = Number(amountInError) || 0;
    if (received > 0) {
      return ((inError / received) * 100).toFixed(2);
    }
    return "0.00";
  };

  useEffect(() => {
    const tempOutcomeBreakdowns = [...props.outcomeBreakdowns] as (
      | TaxYearBreakdown
      | TaxAreaBreakdown
      | TaxYearAreaBreakdown
    )[];
    const items: (
      | TaxYearBreakdown
      | TaxAreaBreakdown
      | TaxYearAreaBreakdown
    )[] =
      props.breakdownType === "Tax area"
        ? tempOutcomeBreakdowns.map((item) => {
            const taxAreaTitle =
              taxAreasMap.find((taxArea) => taxArea.value === item.category)
                ?.label ?? "";
            const adjustedItem = {
              ...item,
              category:
                listTaxAreaData?.taxAreas.taxAreas.find(
                  (taxArea) => taxArea.taxAreaId === item.category
                )?.status === "Active"
                  ? taxAreaTitle
                  : INACTIVE_TAX_AREA_STRING + taxAreaTitle,
              errorRate: calculateErrorRate(
                (item as TaxAreaBreakdown).amountReceived,
                (item as TaxAreaBreakdown).amountInError
              ),
            };
            return adjustedItem;
          })
        : props.breakdownType === "Tax year & area"
        ? tempOutcomeBreakdowns.map((item) => {
            const taxYearAreaItem = item as TaxYearAreaBreakdown;
            const taxAreaTitle =
              taxAreasMap.find(
                (taxArea) => taxArea.value === taxYearAreaItem.taxAreaId
              )?.label ?? "";
            const adjustedItem = {
              ...taxYearAreaItem,
              taxAreaTitle:
                listTaxAreaData?.taxAreas.taxAreas.find(
                  (taxArea) => taxArea.taxAreaId === taxYearAreaItem.taxAreaId
                )?.status === "Active"
                  ? taxAreaTitle
                  : INACTIVE_TAX_AREA_STRING + taxAreaTitle,
            };
            return adjustedItem;
          })
        : tempOutcomeBreakdowns;

    if (props.breakdownType === "Tax year") {
      const totalItem: TaxYearBreakdown = {
        outcomeBreakdownId: "",
        parentId: "",
        type: "Tax year",
        category: "Total",
        totalTaxAmount: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxYearBreakdown).totalTaxAmount),
            0
          )
        ),
        totalPenaltyAmount: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxYearBreakdown).totalPenaltyAmount),
            0
          )
        ),
        totalInterestAmount: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxYearBreakdown).totalInterestAmount),
            0
          )
        ),
        totalOtherAmount: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxYearBreakdown).totalOtherAmount),
            0
          )
        ),
      };
      items.push(totalItem);
    } else if (props.breakdownType === "Tax year & area") {
      const totalItem: TaxYearAreaBreakdown & { taxAreaTitle?: string } = {
        outcomeBreakdownId: "",
        parentId: "",
        type: "Tax year & area",
        category: "Total",
        taxYear: "Total",
        taxAreaId: "",
        taxAreaTitle: "Total",
        totalTaxAmount: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxYearAreaBreakdown).totalTaxAmount),
            0
          )
        ),
        totalPenaltyAmount: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxYearAreaBreakdown).totalPenaltyAmount),
            0
          )
        ),
        totalInterestAmount: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxYearAreaBreakdown).totalInterestAmount),
            0
          )
        ),
        totalOtherAmount: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxYearAreaBreakdown).totalOtherAmount),
            0
          )
        ),
        dueDate: "",
        additionalInformation: "",
      };
      items.push(totalItem);
    } else if (props.breakdownType === "Tax area") {
      const totalItem: TaxAreaBreakdown = {
        outcomeBreakdownId: "",
        parentId: "",
        type: "Tax area",
        category: "Total",
        amountReceived: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxAreaBreakdown).amountReceived),
            0
          )
        ),
        amountInError: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxAreaBreakdown).amountInError),
            0
          )
        ),
        errorRate: "",
        amountAssessed: String(
          items.reduce(
            (sum, item) =>
              sum + Number((item as TaxAreaBreakdown).amountAssessed),
            0
          )
        ),
      };
      totalItem.errorRate = calculateErrorRate(
        totalItem.amountReceived,
        totalItem.amountInError
      );
      items.push(totalItem);
    }

    setSelectedItems(items);
  }, [
    props.outcomeBreakdowns,
    taxAreasMap,
    props.breakdownType,
    listTaxAreaData,
  ]);

  const formatValue = (value: string | number) => {
    return formatMonetaryValue(Number(value), props.currencySign);
  };

  const calculateTotalAmount = (
    item: TaxYearBreakdown | TaxAreaBreakdown | TaxYearAreaBreakdown
  ) => {
    if (item.type === "Tax year" || item.type === "Tax year & area") {
      return formatValue(
        Number(item.totalTaxAmount) +
          Number(item.totalPenaltyAmount) +
          Number(item.totalInterestAmount) +
          Number(item.totalOtherAmount)
      );
    } else {
      return formatValue((item as TaxAreaBreakdown).amountAssessed);
    }
  };

  const rawColumnDefinitions: TableProps<any>["columnDefinitions"] =
    props.breakdownType === "Tax year"
      ? [
          {
            id: "category",
            header: "Year",
            cell: (item: TaxYearBreakdown) =>
              item.category === "Total" ? (
                <strong>{item.category}</strong>
              ) : (
                item.category
              ),
            ariaLabel: createLabelFunction("Year"),
            width: "13%",
          },
          {
            id: "tax",
            header: "Tax",
            cell: (item: TaxYearBreakdown) =>
              item.category === "Total" ? (
                <strong>{formatValue(item.totalTaxAmount)}</strong>
              ) : (
                formatValue(item.totalTaxAmount)
              ),
            ariaLabel: createLabelFunction("Tax"),
            width: "13%",
          },
          {
            id: "penalty",
            header: "Penalty",
            cell: (item: TaxYearBreakdown) =>
              item.category === "Total" ? (
                <strong>{formatValue(item.totalPenaltyAmount)}</strong>
              ) : (
                formatValue(item.totalPenaltyAmount)
              ),
            ariaLabel: createLabelFunction("Penalty"),
            width: "13%",
          },
          {
            id: "interest",
            header: "Interest",
            cell: (item: TaxYearBreakdown) =>
              item.category === "Total" ? (
                <strong>{formatValue(item.totalInterestAmount)}</strong>
              ) : (
                formatValue(item.totalInterestAmount)
              ),
            ariaLabel: createLabelFunction("Interest"),
            width: "13%",
          },
          {
            id: "other",
            header: "Other",
            cell: (item: TaxYearBreakdown) =>
              item.category === "Total" ? (
                <strong>{formatValue(item.totalOtherAmount)}</strong>
              ) : (
                formatValue(item.totalOtherAmount)
              ),
            ariaLabel: createLabelFunction("Other"),
            width: "13%",
          },
          {
            id: "total",
            header: "Total",
            cell: (item: TaxYearBreakdown) =>
              item.category === "Total" ? (
                <strong>{calculateTotalAmount(item)}</strong>
              ) : (
                calculateTotalAmount(item)
              ),
            ariaLabel: createLabelFunction("Total"),
            width: "15%",
          },
        ]
      : props.breakdownType === "Tax year & area"
      ? [
          {
            id: "taxYear",
            header: "Tax Year",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>{item.taxYear}</strong>
              ) : (
                item.taxYear
              ),
            ariaLabel: createLabelFunction("Tax Year"),
            width: "10%",
          },
          {
            id: "taxArea",
            header: "Tax Area",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>{item.taxAreaTitle || "Total"}</strong>
              ) : String(item.taxAreaTitle || "").startsWith(
                  INACTIVE_TAX_AREA_STRING
                ) ? (
                <div>
                  <Popover
                    fixedWidth
                    size="large"
                    content={(item.taxAreaTitle || "")
                      .split(INACTIVE_TAX_AREA_STRING)[1]
                      ?.trim()}
                  >
                    {INACTIVE_TAX_AREA_STRING}
                  </Popover>
                </div>
              ) : (
                item.taxAreaTitle
              ),
            ariaLabel: createLabelFunction("Tax Area"),
            width: "12%",
          },
          {
            id: "tax",
            header: "Tax",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>{formatValue(item.totalTaxAmount)}</strong>
              ) : (
                formatValue(item.totalTaxAmount)
              ),
            ariaLabel: createLabelFunction("Tax"),
            width: "10%",
          },
          {
            id: "penalty",
            header: "Penalty",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>{formatValue(item.totalPenaltyAmount)}</strong>
              ) : (
                formatValue(item.totalPenaltyAmount)
              ),
            ariaLabel: createLabelFunction("Penalty"),
            width: "10%",
          },
          {
            id: "interest",
            header: "Interest",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>{formatValue(item.totalInterestAmount)}</strong>
              ) : (
                formatValue(item.totalInterestAmount)
              ),
            ariaLabel: createLabelFunction("Interest"),
            width: "10%",
          },
          {
            id: "other",
            header: "Other",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>{formatValue(item.totalOtherAmount)}</strong>
              ) : (
                formatValue(item.totalOtherAmount)
              ),
            ariaLabel: createLabelFunction("Other"),
            width: "10%",
          },
          {
            id: "dueDate",
            header: "Due Date",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>-</strong>
              ) : item.dueDate ? (
                formatDateValue(item.dueDate)
              ) : (
                "-"
              ),
            ariaLabel: createLabelFunction("Due Date"),
            width: "10%",
          },
          {
            id: "additionalInformation",
            header: "Additional Info",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>-</strong>
              ) : (
                item.additionalInformation || "-"
              ),
            ariaLabel: createLabelFunction("Additional Info"),
            width: "10%",
          },
          {
            id: "total",
            header: "Total",
            cell: (item: TaxYearAreaBreakdown & { taxAreaTitle?: string }) =>
              item.taxYear === "Total" ? (
                <strong>{calculateTotalAmount(item)}</strong>
              ) : (
                calculateTotalAmount(item)
              ),
            ariaLabel: createLabelFunction("Total"),
            width: "8%",
          },
        ]
      : [
          {
            id: "category",
            header: "Tax Area",
            cell: (item: TaxAreaBreakdown) =>
              item.category === "Total" ? (
                <strong>{item.category}</strong>
              ) : String(item.category).startsWith(INACTIVE_TAX_AREA_STRING) ? (
                <div>
                  <Popover
                    fixedWidth
                    size="large"
                    content={item.category
                      .split(INACTIVE_TAX_AREA_STRING)[1]
                      .trim()}
                  >
                    {INACTIVE_TAX_AREA_STRING}
                  </Popover>
                </div>
              ) : (
                item.category
              ),
            ariaLabel: createLabelFunction("Tax Area"),
            width: "13%",
          },
          {
            id: "amountReceived",
            header: (
              <Popover
                dismissButton={false}
                content={<Box>{LabelPopovers.AMOUNT_REVIEWED}</Box>}
              >
                <strong>{TableLabels.AMOUNT_REVIEWED}</strong>
              </Popover>
            ),
            cell: (item: TaxAreaBreakdown) =>
              item.category === "Total"
                ? null
                : formatValue(item.amountReceived),
            ariaLabel: createLabelFunction(TableLabels.AMOUNT_REVIEWED),
            width: "13%",
          },
          {
            id: "amountInError",
            header: (
              <Popover
                dismissButton={false}
                content={<Box>{LabelPopovers.AMOUNT_IN_ERROR}</Box>}
              >
                <strong>{TableLabels.AMOUNT_IN_ERROR}</strong>
              </Popover>
            ),
            cell: (item: TaxAreaBreakdown) =>
              item.category === "Total" ? (
                <strong>{formatValue(item.amountInError)}</strong>
              ) : (
                formatValue(item.amountInError)
              ),
            ariaLabel: createLabelFunction(TableLabels.AMOUNT_IN_ERROR),
            width: "13%",
          },
          {
            id: "errorRate",
            header: (
              <Popover
                dismissButton={false}
                content={
                  <Box>
                    <div>{LabelPopovers.ERROR_RATE}</div>
                  </Box>
                }
              >
                <strong>{TableLabels.ERROR_RATE}</strong>
              </Popover>
            ),
            cell: (item: TaxAreaBreakdown) =>
              item.category === "Total"
                ? null
                : `${item.errorRate === "NaN" ? "0" : item.errorRate}%`,
            ariaLabel: createLabelFunction(TableLabels.ERROR_RATE),
            width: "13%",
          },
          {
            id: "amountAssessed",
            header: (
              <Popover
                dismissButton={false}
                content={<Box>{LabelPopovers.AMOUNT_ASSESSED}</Box>}
              >
                <strong>{TableLabels.AMOUNT_ASSESSED}</strong>
              </Popover>
            ),
            cell: (item: TaxAreaBreakdown) =>
              item.category === "Total" ? (
                <strong>{formatValue(item.amountAssessed)}</strong>
              ) : (
                formatValue(item.amountAssessed)
              ),
            ariaLabel: createLabelFunction(TableLabels.AMOUNT_ASSESSED),
            width: "13%",
          },
        ];

  const { items, collectionProps } = useCollection(
    Object.values(selectedItems),
    {
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: "category",
          },
        },
      },
    }
  );

  return (
    <div>
      <SpaceBetween size="l">
        <Table
          {...collectionProps}
          header={
            <div style={{ marginBottom: "10px" }}>
              <Header variant="h1">
                {props.overviewType === "Assessment"
                  ? `Assessment by ${props.breakdownType.toLowerCase()}`
                  : `Payment by ${props.breakdownType.toLowerCase()}`}
              </Header>
              <br />
            </div>
          }
          columnDefinitions={rawColumnDefinitions}
          resizableColumns={false}
          items={items}
          variant="container"
        />
      </SpaceBetween>
    </div>
  );
};

export default OutcomeBreakdownTable;
